import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useAgentStore } from '../../store/agentStore.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import StatusBadge from '../shared/StatusBadge.jsx';

// #500: syslog signature alert rules are auto-created on the agent (not
// in cloud D1). This section federates them — read + edit proxy through
// to the agent. The rule's identity (name / signature) is read-only;
// operators tune severity, min_dwell_secs (noise debounce), and enabled.

const SEVERITIES = ['critical', 'warning', 'info'];

export default function SyslogRulesSection() {
  const { agentId } = useAgentStore();
  const { role } = useAuth();
  const canEdit = role === 'org_admin';
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['syslog-rules', agentId],
    queryFn: () => api.get('/alert-rules/syslog', { agent_id: agentId }),
    enabled: !!agentId,
    select: (resp) => resp?.data?.rules ?? [],
  });

  const patchMutation = useMutation({
    mutationFn: ({ ruleId, patch }) =>
      api.patch(`/alert-rules/syslog/${ruleId}`, patch, { agent_id: agentId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['syslog-rules', agentId] }),
  });

  const rules = Array.isArray(data) ? data : [];

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Syslog signature rules</h3>
        <p className="text-xs text-gray-500">
          Auto-created from syslog signatures on the agent. Tune severity, debounce
          (min dwell), and enabled; the name &amp; pattern come from the signature.
          {!canEdit && ' Read-only for your role.'}
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading syslog rules…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Rule</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Severity</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500" title="Minimum seconds a signature must keep matching before the alert fires — raises this to suppress short bursts">Min dwell (s)</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rules.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">
                    No syslog signature rules on this agent yet.
                  </td>
                </tr>
              ) : (
                rules.map((rule) => (
                  <tr key={rule.rule_id}>
                    <td className="px-4 py-3 text-sm">
                      <span className="font-medium text-gray-900">{rule.name}</span>
                      <span
                        className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-purple-700"
                        title="Auto-created from a syslog signature; identity is read-only"
                      >
                        auto
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {canEdit ? (
                        <select
                          value={rule.severity}
                          disabled={patchMutation.isPending}
                          onChange={(e) =>
                            patchMutation.mutate({ ruleId: rule.rule_id, patch: { severity: e.target.value } })
                          }
                          className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                        >
                          {SEVERITIES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      ) : (
                        <StatusBadge status={rule.severity} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {canEdit ? (
                        <input
                          type="number"
                          min="0"
                          max="3600"
                          defaultValue={rule.min_dwell_secs}
                          disabled={patchMutation.isPending}
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (Number.isInteger(v) && v >= 0 && v <= 3600 && v !== rule.min_dwell_secs) {
                              patchMutation.mutate({ ruleId: rule.rule_id, patch: { min_dwell_secs: v } });
                            }
                          }}
                          className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm"
                        />
                      ) : (
                        <span className="font-mono text-gray-700">{rule.min_dwell_secs}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        disabled={!canEdit || patchMutation.isPending}
                        onClick={() =>
                          patchMutation.mutate({ ruleId: rule.rule_id, patch: { enabled: !rule.enabled } })
                        }
                        className={`rounded px-2.5 py-0.5 text-xs font-medium disabled:opacity-50 ${rule.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                      >
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
